/**
 * I-129 Parser
 * Extracts structured data from I-129 (Petition for Nonimmigrant Worker) PDFs
 */

import type { I129Data } from './types';

/**
 * Parse I-129 petition text into structured data
 */
export function parseI129(text: string): I129Data {
  const normalizedText = text.replace(/\r\n/g, '\n');

  return {
    receiptNumber: extractReceiptNumber(normalizedText),
    petitioner: extractPetitionerInfo(normalizedText),
    beneficiary: extractBeneficiaryInfo(normalizedText),
    job: extractJobInfo(normalizedText),
    h1bSupplement: extractH1BSupplement(normalizedText),
    classification: extractClassification(normalizedText),
    requestedAction: extractRequestedAction(normalizedText),
    premiumProcessing: extractPremiumProcessing(normalizedText),
    validFrom: extractValidityDates(normalizedText).from,
    validTo: extractValidityDates(normalizedText).to,
  };
}

/**
 * Extract USCIS receipt number
 */
function extractReceiptNumber(text: string): string | undefined {
  // Receipt numbers follow pattern: 3 letters + 10 digits (e.g., WAC2190012345)
  const patterns = [
    /Receipt Number[:\s]*([A-Z]{3}\d{10})/i,
    /Case Number[:\s]*([A-Z]{3}\d{10})/i,
    /\b(WAC|EAC|LIN|SRC|MSC|IOE)\d{10}\b/,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1] || match[0];
    }
  }
  return undefined;
}

/**
 * Extract petitioner (employer) information
 */
function extractPetitionerInfo(text: string): I129Data['petitioner'] {
  const petitioner: I129Data['petitioner'] = {};

  // Find Part 1 section (Petitioner Information)
  const part1Section = extractSection(text, /Part 1\.?[:\s]*(?:Information About the Petitioner|Petitioner)/i, /Part 2/i);
  const searchText = part1Section || text;

  // Company name
  const companyPatterns = [
    /(?:Company|Petitioner|Employer)(?:'s)?\s*(?:Legal\s+)?Name[:\s]*(.+?)(?:\n|$)/i,
    /(?:Legal Name of Petitioning Entity|Name of Petitioner)[:\s]*(.+?)(?:\n|$)/i,
  ];
  for (const pattern of companyPatterns) {
    const match = searchText.match(pattern);
    if (match) {
      petitioner.companyName = cleanValue(match[1]);
      break;
    }
  }

  // DBA
  const dbaMatch = searchText.match(/(?:DBA|Doing Business As)[:\s]*(.+?)(?:\n|$)/i);
  if (dbaMatch) petitioner.dba = cleanValue(dbaMatch[1]);

  // EIN
  const einMatch = searchText.match(/(?:EIN|Employer Identification Number|FEIN)[:\s]*(\d{2}[\-]?\d{7})/i);
  if (einMatch) petitioner.ein = einMatch[1].replace('-', '');

  // Address
  const addressMatch = searchText.match(/(?:Street Address|Mailing Address|Address)[:\s]*(.+?)(?:\n|$)/i);
  if (addressMatch) petitioner.address = cleanValue(addressMatch[1]);

  // City
  const cityMatch = searchText.match(/(?:City or Town)[:\s]*([A-Za-z\s\-]+?)(?:\n|$|State|ZIP)/i);
  if (cityMatch) petitioner.city = cleanValue(cityMatch[1]);

  // State
  const stateMatch = searchText.match(/(?:State)[:\s]*([A-Z]{2})/i);
  if (stateMatch) petitioner.state = stateMatch[1].toUpperCase();

  // ZIP
  const zipMatch = searchText.match(/(?:ZIP Code|Postal Code)[:\s]*(\d{5}(?:\-\d{4})?)/i);
  if (zipMatch) petitioner.zipCode = zipMatch[1];

  // Phone
  const phoneMatch = searchText.match(/(?:Daytime Phone|Phone|Telephone)[:\s]*(\+?[\d\s\-\(\)]{10,})/i);
  if (phoneMatch) petitioner.phone = cleanValue(phoneMatch[1]);

  // Email
  const emailMatch = searchText.match(/(?:Email)[:\s]*([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/i);
  if (emailMatch) petitioner.email = emailMatch[1].toLowerCase();

  // Business details
  const businessTypeMatch = searchText.match(/(?:Type of Business|Business Type)[:\s]*(.+?)(?:\n|$)/i);
  if (businessTypeMatch) petitioner.businessType = cleanValue(businessTypeMatch[1]);

  const yearEstMatch = searchText.match(/(?:Year Established|Date Established)[:\s]*(\d{4})/i);
  if (yearEstMatch) petitioner.yearEstablished = yearEstMatch[1];

  const employeesMatch = searchText.match(/(?:Current Number of Employees|Total Employees|Number of Employees)[:\s]*(\d+)/i);
  if (employeesMatch) petitioner.currentEmployees = employeesMatch[1];

  const grossIncomeMatch = searchText.match(/(?:Gross Annual Income|Gross Income)[:\s]*\$?([\d,]+)/i);
  if (grossIncomeMatch) petitioner.grossAnnualIncome = grossIncomeMatch[1].replace(/,/g, '');

  const netIncomeMatch = searchText.match(/(?:Net Annual Income|Net Income)[:\s]*\$?([\d,]+)/i);
  if (netIncomeMatch) petitioner.netAnnualIncome = netIncomeMatch[1].replace(/,/g, '');

  return petitioner;
}

/**
 * Extract beneficiary (worker) information
 */
function extractBeneficiaryInfo(text: string): I129Data['beneficiary'] {
  const beneficiary: I129Data['beneficiary'] = {};

  // Find Part 3 section (Beneficiary Information)
  const part3Section = extractSection(text, /Part 3\.?[:\s]*(?:Information About the Beneficiary|Beneficiary)/i, /Part 4/i);
  const searchText = part3Section || text;

  // Name components
  const surnameMatch = searchText.match(/(?:Family Name|Surname|Last Name)[:\s]*([A-Za-z\-\s]+?)(?:\n|$|Given)/i);
  if (surnameMatch) beneficiary.surname = cleanName(surnameMatch[1]);

  const givenMatch = searchText.match(/(?:Given Name|First Name)[:\s]*([A-Za-z\-\s]+?)(?:\n|$|Middle)/i);
  if (givenMatch) beneficiary.givenName = cleanName(givenMatch[1]);

  const middleMatch = searchText.match(/(?:Middle Name)[:\s]*([A-Za-z\-\s]+?)(?:\n|$)/i);
  if (middleMatch) beneficiary.middleName = cleanName(middleMatch[1]);

  // Date of birth
  const dobMatch = searchText.match(/(?:Date of Birth|DOB)[:\s]*(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4}|\w+\s+\d{1,2},?\s+\d{4})/i);
  if (dobMatch) beneficiary.dateOfBirth = dobMatch[1];

  // Country of birth
  const cobMatch = searchText.match(/(?:Country of Birth)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
  if (cobMatch) beneficiary.countryOfBirth = cleanValue(cobMatch[1]);

  // Country of citizenship
  const cocMatch = searchText.match(/(?:Country of Citizenship|Citizenship)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
  if (cocMatch) beneficiary.countryOfCitizenship = cleanValue(cocMatch[1]);

  // Gender
  const genderMatch = searchText.match(/(?:Sex|Gender)[:\s]*(Male|Female|M|F)/i);
  if (genderMatch) {
    beneficiary.gender = genderMatch[1].toLowerCase().startsWith('m') ? 'Male' : 'Female';
  }

  // Immigration numbers
  const alienMatch = searchText.match(/(?:Alien Number|A-Number|A#)[:\s]*A?(\d{8,9})/i);
  if (alienMatch) beneficiary.alienNumber = alienMatch[1];

  const i94Match = searchText.match(/(?:I-94 Number|Arrival\/Departure Number)[:\s]*(\d{11})/i);
  if (i94Match) beneficiary.i94Number = i94Match[1];

  // Passport
  const passportMatch = searchText.match(/(?:Passport Number)[:\s]*([A-Z0-9]+)/i);
  if (passportMatch) beneficiary.passportNumber = passportMatch[1].toUpperCase();

  const passportExpMatch = searchText.match(/(?:Passport Expiration|Passport Expires)[:\s]*(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (passportExpMatch) beneficiary.passportExpiration = passportExpMatch[1];

  // Current immigration status
  const statusMatch = searchText.match(/(?:Current Immigration Status|Current Status)[:\s]*([A-Z]\-?\d?[A-Z]?)/i);
  if (statusMatch) beneficiary.currentStatus = statusMatch[1];

  const statusExpMatch = searchText.match(/(?:Status Expires|Authorized Stay Expires)[:\s]*(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (statusExpMatch) beneficiary.statusExpiration = statusExpMatch[1];

  // Current address
  const addressMatch = searchText.match(/(?:Current U\.?S\.? Address|U\.?S\.? Address|Address)[:\s]*(.+?)(?:\n|$)/i);
  if (addressMatch) beneficiary.address = cleanValue(addressMatch[1]);

  const cityMatch = searchText.match(/(?:City)[:\s]*([A-Za-z\s\-]+?)(?:\n|$|State)/i);
  if (cityMatch) beneficiary.city = cleanValue(cityMatch[1]);

  const stateMatch = searchText.match(/(?:State)[:\s]*([A-Z]{2})/i);
  if (stateMatch) beneficiary.state = stateMatch[1].toUpperCase();

  const zipMatch = searchText.match(/(?:ZIP Code)[:\s]*(\d{5}(?:\-\d{4})?)/i);
  if (zipMatch) beneficiary.zipCode = zipMatch[1];

  return beneficiary;
}

/**
 * Extract job information
 */
function extractJobInfo(text: string): I129Data['job'] {
  const job: I129Data['job'] = {};

  // Find Part 5 section (Job Information)
  const part5Section = extractSection(text, /Part 5\.?[:\s]*(?:Basic Information|Proposed Employment|Job)/i, /Part 6/i);
  const searchText = part5Section || text;

  // Job title
  const titleMatch = searchText.match(/(?:Job Title|Position Title|Title)[:\s]*(.+?)(?:\n|$)/i);
  if (titleMatch) job.title = cleanValue(titleMatch[1]);

  // SOC Code
  const socMatch = searchText.match(/(?:SOC Code|SOC\/O\*NET Code|Occupational Code)[:\s]*(\d{2}[\-]?\d{4}(?:\.\d{2})?)/i);
  if (socMatch) job.socCode = socMatch[1];

  // Employment dates
  const startMatch = searchText.match(/(?:Start Date|From Date|Employment Start)[:\s]*(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (startMatch) job.startDate = startMatch[1];

  const endMatch = searchText.match(/(?:End Date|To Date|Employment End)[:\s]*(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (endMatch) job.endDate = endMatch[1];

  // Full/Part time
  const fullTimeMatch = searchText.match(/(?:Full-?Time|Part-?Time)[:\s]*(Yes|No|Full|Part)/i);
  if (fullTimeMatch) {
    job.fullTime = /yes|full/i.test(fullTimeMatch[1]);
  }

  // Hours per week
  const hoursMatch = searchText.match(/(?:Hours Per Week|Weekly Hours)[:\s]*(\d+)/i);
  if (hoursMatch) job.hoursPerWeek = hoursMatch[1];

  // Wage
  const wagePatterns = [
    /(?:Wage|Salary|Rate of Pay)[:\s]*\$?([\d,]+(?:\.\d{2})?)\s*(?:per\s+)?(hour|week|month|year|annually)/i,
    /\$?([\d,]+(?:\.\d{2})?)\s*\/\s*(hour|week|month|year|hr|wk|mo|yr)/i,
  ];
  for (const pattern of wagePatterns) {
    const match = searchText.match(pattern);
    if (match) {
      job.wageAmount = match[1].replace(/,/g, '');
      const unit = match[2].toLowerCase();
      if (unit.startsWith('h')) job.wageUnit = 'hour';
      else if (unit.startsWith('w')) job.wageUnit = 'week';
      else if (unit.startsWith('m')) job.wageUnit = 'month';
      else job.wageUnit = 'year';
      break;
    }
  }

  // Worksite
  const worksiteAddressMatch = searchText.match(/(?:Worksite Address|Place of Employment)[:\s]*(.+?)(?:\n|$)/i);
  if (worksiteAddressMatch) job.worksiteAddress = cleanValue(worksiteAddressMatch[1]);

  const worksiteCityMatch = searchText.match(/(?:Worksite City|City)[:\s]*([A-Za-z\s\-]+?)(?:\n|$|State)/i);
  if (worksiteCityMatch) job.worksiteCity = cleanValue(worksiteCityMatch[1]);

  const worksiteStateMatch = searchText.match(/(?:Worksite State|State)[:\s]*([A-Z]{2})/i);
  if (worksiteStateMatch) job.worksiteState = worksiteStateMatch[1].toUpperCase();

  const worksiteZipMatch = searchText.match(/(?:Worksite ZIP|ZIP)[:\s]*(\d{5}(?:\-\d{4})?)/i);
  if (worksiteZipMatch) job.worksiteZip = worksiteZipMatch[1];

  // Job duties
  const dutiesMatch = searchText.match(/(?:Job Duties|Duties|Description of Duties)[:\s]*(.+?)(?:\n\n|Part\s+\d|$)/is);
  if (dutiesMatch) job.duties = cleanValue(dutiesMatch[1]);

  // Education requirements
  const eduReqMatch = searchText.match(/(?:Education Required|Minimum Education)[:\s]*(.+?)(?:\n|$)/i);
  if (eduReqMatch) job.educationRequired = cleanValue(eduReqMatch[1]);

  const fieldMatch = searchText.match(/(?:Field of Study|Major)[:\s]*(.+?)(?:\n|$)/i);
  if (fieldMatch) job.fieldOfStudy = cleanValue(fieldMatch[1]);

  const expMatch = searchText.match(/(?:Years of Experience|Experience Required)[:\s]*(\d+)/i);
  if (expMatch) job.yearsExperience = expMatch[1];

  return job;
}

/**
 * Extract H-1B supplement information
 */
function extractH1BSupplement(text: string): I129Data['h1bSupplement'] | undefined {
  // Check if this is an H-1B petition
  if (!/H-?1B/i.test(text)) {
    return undefined;
  }

  const supplement: I129Data['h1bSupplement'] = {};

  // Find H-1B supplement section
  const h1bSection = extractSection(text, /H-?1B(?:\s+Data Collection|\s+Supplement)/i, /(?:Part\s+\d|Signature)/i);
  const searchText = h1bSection || text;

  // Specialty occupation
  const specialtyMatch = searchText.match(/(?:Specialty Occupation)[:\s]*(.+?)(?:\n|$)/i);
  if (specialtyMatch) supplement.specialty = cleanValue(specialtyMatch[1]);

  // LCA number
  const lcaMatch = searchText.match(/(?:LCA(?:\s+Case)?\s+Number|ETA Case Number)[:\s]*([A-Z]\-\d{3}\-\d{5}\-\d{6,7})/i);
  if (lcaMatch) supplement.lcaNumber = lcaMatch[1];

  // Wages
  const lcaWageMatch = searchText.match(/(?:LCA Wage|Wage Rate on LCA)[:\s]*\$?([\d,]+(?:\.\d{2})?)/i);
  if (lcaWageMatch) supplement.lcaWage = lcaWageMatch[1].replace(/,/g, '');

  const actualWageMatch = searchText.match(/(?:Actual Wage)[:\s]*\$?([\d,]+(?:\.\d{2})?)/i);
  if (actualWageMatch) supplement.actualWage = actualWageMatch[1].replace(/,/g, '');

  const prevailingWageMatch = searchText.match(/(?:Prevailing Wage)[:\s]*\$?([\d,]+(?:\.\d{2})?)/i);
  if (prevailingWageMatch) supplement.prevailingWage = prevailingWageMatch[1].replace(/,/g, '');

  // Only return if we found H-1B specific data
  if (Object.keys(supplement).length > 0) {
    return supplement;
  }
  return undefined;
}

/**
 * Extract classification type
 */
function extractClassification(text: string): string | undefined {
  const patterns = [
    /(?:Classification Sought|Requested Classification)[:\s]*([A-Z]\-?\d[A-Z]?)/i,
    /(?:Visa Classification|Nonimmigrant Classification)[:\s]*([A-Z]\-?\d[A-Z]?)/i,
    /\b(H-?1B|L-?1[AB]|O-?1[AB]?|TN|E-?[123]|P-?1[AB]?)\b/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1].toUpperCase().replace(/(\w)(\d)/, '$1-$2');
    }
  }
  return undefined;
}

/**
 * Extract requested action
 */
function extractRequestedAction(text: string): string | undefined {
  const patterns = [
    /(?:Requested Action|Type of Petition)[:\s]*(New|Extension|Amendment|Change of Employer|Change of Status|Concurrent)/i,
    /(?:Petition Type)[:\s]*(New|Extension|Amendment)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1];
    }
  }

  // Infer from context
  if (/new employment|initial/i.test(text)) return 'New';
  if (/extension/i.test(text)) return 'Extension';
  if (/amendment|change/i.test(text)) return 'Amendment';

  return undefined;
}

/**
 * Extract premium processing status
 */
function extractPremiumProcessing(text: string): boolean {
  return /Premium Processing|I-907/i.test(text);
}

/**
 * Extract validity dates
 */
function extractValidityDates(text: string): { from?: string; to?: string } {
  const result: { from?: string; to?: string } = {};

  const fromMatch = text.match(/(?:Valid From|Petition Valid From|Employment From)[:\s]*(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (fromMatch) result.from = fromMatch[1];

  const toMatch = text.match(/(?:Valid To|Petition Valid To|Employment To|Valid Until)[:\s]*(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (toMatch) result.to = toMatch[1];

  return result;
}

// Helper functions

function extractSection(text: string, startPattern: RegExp, endPattern: RegExp): string | null {
  const startMatch = text.match(startPattern);
  if (!startMatch) return null;

  const startIndex = startMatch.index! + startMatch[0].length;
  const remainingText = text.substring(startIndex);

  const endMatch = remainingText.match(endPattern);
  const endIndex = endMatch ? endMatch.index! : remainingText.length;

  return remainingText.substring(0, endIndex);
}

function cleanValue(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function cleanName(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^A-Za-z\s\-']/g, '');
}
